import { resolveWpMode, type WpMode } from '@guard-dog/contracts';
import { useState } from 'react';
import { ApiError, api, type ConsoleSite, type WpModeDelivery } from '../api/client';
import { canMoveToWpMode } from '../lib/entitlement';

/**
 * Ordered least-to-most action, which is the order a site is rolled out in. Not the edge's
 * `enforce|monitor|bypass`: `bypass` describes a proxy forwarding traffic untouched and there is no
 * proxy in this plane, so the vocabularies are kept apart on purpose (ADR-0030 §1).
 */
const MODES: readonly WpMode[] = ['off', 'monitor', 'enforce'];

export function wpModeDeliveryMessage(delivery: WpModeDelivery): string {
  if (delivery.status === 'applied') {
    return `Applied by the connector now${delivery.observedWpMode ? ` (${delivery.observedWpMode} observed)` : ''}.`;
  }
  if (delivery.reason === 'unsupported_connector') {
    return 'Saved; this connector will apply it through the scheduled sync fallback.';
  }
  return `Saved; signed push did not confirm (${delivery.reason}). Scheduled sync fallback is pending.`;
}

/**
 * What the plugin's own rule engine may do on this site (ADR-0030). Owner/admin can switch it;
 * everyone else sees it read-only.
 *
 * Shown for EVERY site, unlike `EdgeControl`. A site with no edge config has `edgeMode: undefined`
 * and that is correct — but most of the fleet is in exactly that state, and it is precisely the
 * population a WordPress-plane switch exists for.
 */
export function WpModeControl({
  site,
  canEdit,
  canAddProtection,
  onChanged,
}: {
  site: ConsoleSite;
  canEdit: boolean;
  canAddProtection: boolean;
  onChanged: () => void;
}) {
  const [pending, setPending] = useState<WpMode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [delivery, setDelivery] = useState<WpModeDelivery | null>(null);

  // An absent `wpMode` is `monitor` — the engine runs, records would-blocks, and denies nothing. It
  // is not "unknown" and not "none": every site in the fleet has the column unset right now, and
  // rendering that absence as an empty state would tell an operator a working site is unprotected.
  const current = resolveWpMode(site.wpMode);
  const enforcePaused = !canMoveToWpMode({ current, next: 'enforce', canAddProtection });

  if (!canEdit) return <span className={`badge badge-${current}`}>{current}</span>;

  async function change(mode: WpMode) {
    if (mode === current || pending) return;
    if (!canMoveToWpMode({ current, next: mode, canAddProtection })) return;
    setPending(mode);
    setError(null);
    setDelivery(null);
    try {
      const result = await api.setSiteWpMode(site.id, mode);
      setDelivery(result.delivery);
      onChanged();
    } catch (e) {
      setError(
        e instanceof ApiError && (e.status === 401 || e.status === 403)
          ? 'Not authorized.'
          : 'Failed to change mode.',
      );
    } finally {
      setPending(null);
    }
  }

  return (
    <div className="wp-mode-control">
      <fieldset className="segmented">
        <legend className="sr-only">WordPress enforcement mode</legend>
        {MODES.map((m) => {
          const paused = !canMoveToWpMode({ current, next: m, canAddProtection });
          return (
            <button
              key={m}
              type="button"
              className={m === current ? 'active' : ''}
              disabled={pending !== null || m === current || paused}
              title={paused ? `Moving to ${m} is paused until the plan is active.` : undefined}
              onClick={() => change(m)}
            >
              {pending === m ? '…' : m}
            </button>
          );
        })}
      </fieldset>
      <span className="muted">
        {delivery
          ? wpModeDeliveryMessage(delivery)
          : 'Signed push when supported; scheduled sync is the fallback.'}
      </span>
      {error && <span className="error">{error}</span>}
      {enforcePaused && <span className="restriction-note">Enforce paused until plan active.</span>}
    </div>
  );
}
